import { useCallback, useState } from 'react'
import { useMutation } from '@apollo/client/react'
import { toast } from '@buffer-mono/popcorn'

import { graphql } from '../../../gql'
import type {
  PaymentActionRequired,
  ScheduledSubscriptionUpdateV2,
  SubscriptionUpdateStatus,
} from '../../../gql/graphql'
import { useOrganizationId } from '../../../hooks/useCurrentOrganization'
import { QUERY_ACCOUNT } from '../../../common/graphql/account'
import { ORGANIZATION_ACTIONS } from '../../../common/events/orgEvents'
import { useAccount } from '../../../hooks/useAccount'
import { GetPlanSelectorData } from './usePlanSelectorQuery'

const UpdateSubscriptionQuantity = graphql(/* GraphQL */ `
  mutation UpdateSubscriptionQuantity(
    $organizationId: OrganizationId!
    $quantity: Int!
    $attribution: AttributionInput
  ) {
    updateSubscriptionQuantity(
      input: {
        organizationId: $organizationId
        quantity: $quantity
        attribution: $attribution
      }
    ) {
      ... on UpdateBillingSubscriptionPlanResponse {
        status
        scheduledUpdate {
          newPlan
          newInterval
          newQuantity
          effectiveDate
        }
        paymentAction {
          clientSecret
          paymentMethodId
        }
      }
      ... on VoidMutationError {
        message
      }
    }
  }
`)

interface UseUpdateSubscriptionQuantityParams {
  quantity: number
  attribution?: {
    cta?: string
    upgradePathName?: string
  }
}

interface UseUpdateSubscriptionQuantityReturn {
  updateQuantity: (params: UseUpdateSubscriptionQuantityParams) => Promise<{
    status: SubscriptionUpdateStatus
    scheduledUpdate?: ScheduledSubscriptionUpdateV2 | null
    paymentAction?: PaymentActionRequired | null
  } | void>
  loading: boolean
  error: Error | null
}

export function useUpdateSubscriptionQuantity(): UseUpdateSubscriptionQuantityReturn {
  const organizationId = useOrganizationId()
  const { account } = useAccount()
  const [error, setError] = useState<Error | null>(null)

  const [updateSubscriptionQuantity, { loading }] = useMutation(
    UpdateSubscriptionQuantity,
    {
      refetchQueries: [{ query: QUERY_ACCOUNT }, GetPlanSelectorData],
      awaitRefetchQueries: true,
    },
  )

  const updateQuantity = useCallback(
    async ({ quantity, attribution }: UseUpdateSubscriptionQuantityParams) => {
      if (!organizationId) {
        throw new Error('Organization ID is required')
      }

      setError(null)

      try {
        const result = await updateSubscriptionQuantity({
          variables: {
            organizationId,
            quantity,
            attribution,
          },
        })

        const response = result.data?.updateSubscriptionQuantity

        if (response?.__typename === 'VoidMutationError') {
          const errorMessage = response.message || 'Unknown error occurred'
          toast.error('Sorry, there was an error updating your subscription')
          throw new Error(errorMessage)
        }

        if (response?.__typename === 'UpdateBillingSubscriptionPlanResponse') {
          ORGANIZATION_ACTIONS.billingUpdated({ account })
          return {
            status: response.status,
            scheduledUpdate: response.scheduledUpdate,
            paymentAction: response.paymentAction,
          }
        }

        if (result.error) {
          toast.error('Sorry, there was an error updating your subscription')
          throw new Error(result.error.message)
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error occurred'
        setError(new Error(errorMessage))
        toast.error('Sorry, there was an error updating your subscription')
        throw err
      }
    },
    [organizationId, updateSubscriptionQuantity, account],
  )

  return {
    updateQuantity,
    loading,
    error,
  }
}
